import { Card, CardContent, CardHeader, CardTitle } from '@pesto/ui/components/Card';
import { Typography } from '@pesto/ui/components/Typography';
import { cn } from '@pesto/utils';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { useMemo } from 'react';

import type { LoanDataUploadErrorRow, LoanDataUploadResponse } from './types';

const isErrorRow = (row: LoanDataUploadResponse['results'][number]): row is LoanDataUploadErrorRow =>
  row.status === 'error';

const identifyRow = (row: LoanDataUploadErrorRow): string => {
  if (row.partner_account_number) return row.partner_account_number;

  if (row.row != null) return `Row ${row.row}`;

  return 'Unknown row';
};

type GroupedErrors = {
  error: string;
  rows: LoanDataUploadErrorRow[];
};

const groupByError = (errors: LoanDataUploadErrorRow[]): GroupedErrors[] => {
  const buckets = new Map<string, LoanDataUploadErrorRow[]>();

  for (const row of errors) {
    const list = buckets.get(row.error) ?? [];
    list.push(row);
    buckets.set(row.error, list);
  }

  return Array.from(buckets.entries()).map(([error, rows]) => ({ error, rows }));
};

interface LoanDataUploadResultProps {
  result: LoanDataUploadResponse;
  className?: string;
}

export const LoanDataUploadResult = ({ result, className }: LoanDataUploadResultProps) => {
  const errors = useMemo(() => result.results.filter(isErrorRow), [result.results]);
  const grouped = useMemo(() => groupByError(errors), [errors]);
  const hasErrors = errors.length > 0;
  const allFailed = result.failure_count > 0 && result.success_count === 0;

  return (
    <Card className={cn('w-full', className)} data-testid="loan-data-upload-result">
      <CardHeader className="space-y-1.5">
        <div className="flex items-center gap-2">
          {hasErrors ? (
            <AlertCircle className={cn('h-5 w-5', allFailed ? 'text-destructive' : 'text-amber-500')} />
          ) : (
            <CheckCircle2 className="h-5 w-5 text-emerald-500" />
          )}
          <CardTitle className="text-lg">
            {result.success_count} of {result.total} loans uploaded successfully
          </CardTitle>
        </div>
        <Typography variant="bodySmall" className="text-muted-foreground">
          {result.failure_count > 0
            ? `${result.failure_count} ${result.failure_count === 1 ? 'row' : 'rows'} did not upload. See the breakdown below.`
            : 'All rows were processed without errors.'}
        </Typography>
      </CardHeader>
      {hasErrors && (
        <CardContent className="space-y-4">
          {grouped.map(({ error, rows }) => (
            <div key={error} className="border-border bg-muted/40 rounded-md border p-3">
              <Typography variant="bodySmall" className="text-foreground font-medium">
                {error}{' '}
                <span className="text-muted-foreground font-normal">
                  ({rows.length} {rows.length === 1 ? 'row' : 'rows'})
                </span>
              </Typography>
              <ul className="text-muted-foreground mt-2 flex flex-wrap gap-x-3 gap-y-1 font-mono text-xs">
                {rows.map((row, index) => (
                  <li key={`${row.partner_account_number ?? row.row ?? index}-${index}`}>{identifyRow(row)}</li>
                ))}
              </ul>
            </div>
          ))}
        </CardContent>
      )}
    </Card>
  );
};
